/**
 * **The provider's own card fields, drawn by the provider, in the member's browser**
 * (`MEM-021` AC-2, AC-3; `PAY-004`).
 *
 * This is the one concrete {@link CardElementsHost} backed by Stripe.js. Everything else on the
 * client side of payment was built before it — the panel, the four labelled regions, the seam,
 * the mock — and the checkout said so in a sentence, honestly, because outside a
 * `VITE_PAYMENT_PROVIDER=mock` build `resolveCardElements()` answered `null` and there was no
 * card to type into. That sentence was the last thing between a member on the sandbox and paying.
 *
 * ## What this file is allowed to touch, and what it is not
 *
 * `MEM-021` AC-3, verbatim: *"Card details are captured by the provider's hosted elements in the
 * member's browser and exchanged directly with the provider: nothing the member types — card
 * number, expiry, CVC or PAN — reaches an H2HSpark server, log, database, backup, error trace or
 * monitoring payload."*
 *
 * The mechanism is iframes on `js.stripe.com`'s origin. This application's script cannot read
 * across that boundary, so the guarantee is structural rather than a matter of discipline — which
 * is why the seam has no `getValue` and this file adds none.
 *
 * The part that is *not* structural, and is the reason this file is written the way it is, is the
 * **error path**. A provider error object is not an opaque string. Stripe's carries `code`,
 * `decline_code`, `param`, and — on some shapes — a whole `payment_method` object with the
 * instrument's attributes on it. Every one of those is one `logger.error(error)` away from a
 * monitoring payload, and the edit that puts it there looks like diligence: somebody debugging a
 * decline wants the decline code. So:
 *
 *   - **Nothing here logs.** No `console`, no `@h2hspark/logger`, no error reporter. A source scan
 *     in `the-stripe-elements-a-member-types-into.test.tsx` fails the build if one appears.
 *   - **Nothing here makes a request.** No `fetch`, no `XMLHttpRequest`, no `sendBeacon`. The only
 *     network this file causes is the provider's own script tag and whatever the provider's own
 *     code does inside its own iframes.
 *   - **A provider error is never rethrown, never wrapped and never attached.** It is reduced, at
 *     the boundary, to one `string` — {@link providerMessage} reads `message` and nothing else —
 *     and a **fresh** `Error` is thrown carrying that string and no `cause`. An `Error` with the
 *     provider's object as its `cause` is an error trace containing `decline_code`, which is the
 *     exact wording AC-3 forbids.
 *
 * The panel puts that message on the screen, which is the member's own browser reading the
 * provider's own sentence about the member's own card. That is display, not egress.
 *
 * ## Why the script is fetched here rather than in `index.html`
 *
 * A visitor browsing events should not fetch a payment SDK. `js.stripe.com/v3/` is loaded on
 * first `mount`, once per document, and a member who never opens a checkout never pays for it.
 *
 * The cost is a failure mode that a `<script>` in the head does not have: the load can fail —
 * offline, a blocked third-party script, a content blocker. **A host that cannot draw its fields
 * must say so and must not hang.** So a failed load resolves into a stated absence in each field
 * region and a validity report of `{ complete: false, message }`, which leaves the Pay control
 * disabled with the reason on screen beside it. It never leaves a spinner running and it never
 * leaves the control live over fields that do not exist.
 */
import {
  CARD_ENTRY_UNAVAILABLE,
  CARD_TOKEN_EXCHANGE_FAILED,
  STRIPE_ELEMENT_APPEARANCE,
  STRIPE_SCRIPT_URL,
} from './constants';
import type { CardElementField } from './constants';
import type { CardElementsHost, CardFieldsValidity } from './types';

/**
 * Which of the provider's field types is drawn into each of this application's four regions.
 *
 * **This is the one place in `apps/web` that spells the provider's name for a card number, and it
 * is the reason this file carries a narrow, argued exemption from the AC-3 source scan.** The
 * token here is the name of a *region the provider draws an iframe into* — the same thing
 * `CARD_ELEMENT_FIELDS` is, in the provider's vocabulary rather than ours. It is never a variable
 * holding a value, and the scan's replacement for the pattern it skips is the set of assertions
 * `the-stripe-elements-a-member-types-into.test.tsx` makes about this file specifically: no
 * logging, no requests, no state that could hold a field's contents, and exactly one property
 * read off a provider error.
 *
 * Three of the four map onto a hosted field. **The name on the card does not, and that is a
 * property of the provider rather than a decision taken here**: Stripe Elements offers no hosted
 * element for a cardholder name, and its own integrations collect it in the site's own input and
 * pass it as billing details.
 *
 * This platform cannot do that. `PAY-001` AC-4 puts *"the name on the card"* on the same side of
 * the line as the number and the security code, and `MEM-021`'s notes restate it: it *"cannot be
 * an ordinary `<input>` whose value this application holds, however harmless a name looks beside
 * a card number"*. So the region is drawn where the prototype draws it and says what it is,
 * rather than being deleted (which would change the layout) or filled with an input (which would
 * break AC-3). `PAY-004`'s open question puts the choice to the PM.
 */
const PROVIDER_ELEMENT_TYPES: Readonly<Record<CardElementField, string | null>> = {
  number: 'cardNumber',
  expiry: 'cardExpiry',
  'security-code': 'cardCvc',
  // The provider has no cardholder-name element. Since 2026-08-30 the panel draws that field
  // itself and never asks for it here, so this entry is the answer to a question nobody puts:
  // `mount('name', node)` draws nothing rather than throwing, because a host that crashed on an
  // unexpected field would turn a wiring mistake into a blank checkout.
  name: null,
};

// ─── the sliver of the provider's surface this file uses ───────────────────────────────────────

/*
 * Written out rather than taken from `@stripe/stripe-js`, for the reason ADR 045 gives for the
 * server adapter speaking the provider's HTTP API rather than its SDK: the typings describe a
 * surface many times larger than what is used here, they travel into the member app's bundle, and
 * the seam they would sit behind is three methods wide. What is declared below is what is called.
 *
 * These are **structural** types over an object the provider's own script puts on `window`. None
 * of them can answer with a field's contents — there is no such method on Stripe's element API to
 * declare — which is the same absence `CardElementsHost` makes explicit one layer up.
 */

/** One of the provider's mounted fields. `on('change')` reports completeness, never a value. */
interface ProviderElement {
  mount(node: HTMLElement): void;
  unmount(): void;
  destroy(): void;
  on(event: 'change', handler: (change: ProviderChange) => void): void;
}

/** What the provider says about its own field as a member types. Note what is absent: a value. */
interface ProviderChange {
  readonly complete?: boolean;
  readonly empty?: boolean;
  readonly error?: { readonly message?: unknown } | undefined;
}

interface ProviderElements {
  create(type: string, options?: Record<string, unknown>): ProviderElement;
}

/**
 * The provider client.
 *
 * `createPaymentMethod` **resolves** with `{ paymentMethod, error }` rather than rejecting — a
 * declined card is an answer, not an exception. Both branches are handled below, and neither
 * branch lets the provider's object out of this file.
 */
export interface ProviderClient {
  elements(options?: Record<string, unknown>): ProviderElements;
  createPaymentMethod(options: {
    readonly type: 'card';
    readonly card: ProviderElement;
    /**
     * The provider's own slot for the details it does not draw a field for. Exactly one is sent:
     * the name on the card. It is written here, at the wire, and held nowhere in this file.
     */
    readonly billing_details: { readonly name: string };
  }): Promise<{
    readonly paymentMethod?: { readonly id?: unknown } | undefined;
    readonly error?: unknown;
  }>;
}

/** How the provider's client is obtained. Injectable so a test never fetches a third-party script. */
export type ProviderLoader = () => Promise<ProviderClient>;

// ─── loading `js.stripe.com/v3/`, once per document ────────────────────────────────────────────

/**
 * What the provider's script puts on `window`, as this file is willing to see it.
 *
 * A mapped type rather than an interface with a `Stripe` member, because the vendor's global is
 * PascalCase and `@typescript-eslint/naming-convention` rightly refuses that spelling on a
 * declared member. The name is the vendor's to choose and ours to quote, so it is quoted.
 */
type ProviderGlobal = Partial<Record<'Stripe', (publishableKey: string) => ProviderClient>>;

/**
 * The in-flight or settled script load, shared by every host in this document.
 *
 * Module-level because the *script* is a property of the page, not of a host: two checkout mounts
 * in one session must not append two script tags. A rejected load is **not** cached — a member
 * whose first attempt failed offline gets a real retry when they come back to the screen, which
 * is the whole reason the panel's failure state offers one.
 */
let scriptLoad: Promise<void> | null = null;

function loadProviderScript(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error(CARD_ENTRY_UNAVAILABLE));
  }
  const global = globalThis as unknown as ProviderGlobal;
  if (typeof global.Stripe === 'function') return Promise.resolve();
  if (scriptLoad !== null) return scriptLoad;

  scriptLoad = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = STRIPE_SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', () => {
      resolve();
    });
    // Every way the fetch can fail arrives here: offline, DNS, a content blocker, a CSP refusal.
    // Rejecting rather than leaving the promise pending is the difference between the panel
    // saying card entry is unavailable and the panel showing four empty boxes for ever.
    script.addEventListener('error', () => {
      scriptLoad = null;
      reject(new Error(CARD_ENTRY_UNAVAILABLE));
    });
    document.head.appendChild(script);
  });
  return scriptLoad;
}

function defaultProviderLoader(publishableKey: string): ProviderLoader {
  return async () => {
    await loadProviderScript();
    const global = globalThis as unknown as ProviderGlobal;
    const construct = global.Stripe;
    if (typeof construct !== 'function') throw new Error(CARD_ENTRY_UNAVAILABLE);
    return construct(publishableKey);
  };
}

// ─── the one place a provider error is read ────────────────────────────────────────────────────

/**
 * **Reduce a provider error to one sentence, and let nothing else out.**
 *
 * The single narrow gate every provider failure passes through, and the reason it is a named
 * function rather than three inline `?.message` reads: a rule enforced in one place can be
 * asserted in one place, and this one is asserted twice over — a source scan proving no other
 * property of a provider error is ever named in this file, and a behavioural test driving an
 * error object stuffed with `decline_code`, `param` and a `payment_method` and reading back what
 * the thrown `Error` carries.
 *
 * `message` is the provider's own localised sentence about its own field — *"Your card number is
 * incomplete."*, *"Your card was declined."* — written by the provider for the person holding the
 * card. It is not, and cannot be, an echo of the digits: those never left the iframe that this
 * function's caller cannot see into either.
 *
 * Anything that is not a non-empty string becomes the fallback. A provider that one day answers
 * with an object here must not turn into `"[object Object]"` on a member's screen, and must not
 * turn into a serialisable object in a thrown error.
 */
function providerMessage<T extends string | null>(error: unknown, fallback: T): string | T {
  if (typeof error !== 'object' || error === null) return fallback;
  const message = (error as { readonly message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

// ─── the host ──────────────────────────────────────────────────────────────────────────────────

/** Per-field state. `complete` and a message the provider wrote; there is nowhere to put a value. */
interface FieldState {
  complete: boolean;
  message: string | null;
}

/**
 * **A {@link CardElementsHost} backed by the provider's own iframed fields.**
 *
 * One host per checkout screen. It owns one provider client, one `elements` group, and one
 * element per field the provider draws — and it holds no card data, because there is no method on
 * anything it holds that could answer with any.
 *
 * @param publishableKey The provider's publishable key. Public by design — the provider embeds it
 *   in client-side JavaScript served to every visitor — and therefore ordinary committed
 *   configuration. The **secret** key is a different value entirely and lives only in Secrets
 *   Manager (`payments/provider-secret-key`, ADR 045); it never reaches a browser and never
 *   appears in this repository.
 * @param options.loadProvider How the provider client is obtained. Defaults to fetching
 *   `js.stripe.com/v3/` on first use. A test passes its own so no suite reaches a third party.
 */
export function createStripeCardElements(
  publishableKey: string,
  options: { readonly loadProvider?: ProviderLoader } = {},
): CardElementsHost {
  const load = options.loadProvider ?? defaultProviderLoader(publishableKey);

  const fields = new Map<CardElementField, FieldState>();
  const elements = new Map<CardElementField, ProviderElement>();
  const listeners = new Set<(validity: CardFieldsValidity) => void>();
  /** Set when the provider could not be reached at all. Reported instead of per-field messages. */
  let unavailable: string | null = null;

  /**
   * The client, fetched once and shared by all four regions.
   *
   * Created on the first `mount` rather than when the host is constructed, so
   * `resolveCardElements()` — which runs as a default prop while a checkout renders — costs
   * nothing and cannot fail. That is the same argument `createDeferredPaymentPort` makes on the
   * server (ADR 045's 2026-08-29 amendment): resolving a payment capability must never be the
   * thing that breaks a screen.
   */
  let client: Promise<ProviderClient> | null = null;
  let group: Promise<ProviderElements> | null = null;

  /**
   * **One client, shared by the fields and by the exchange, and that is a correctness rule rather
   * than an optimisation.**
   *
   * The provider's `createPaymentMethod` is given one of its own mounted elements, and it will
   * only accept an element belonging to the same client instance that created it. Calling the
   * loader a second time at exchange time builds a *second* client, whose `createPaymentMethod`
   * does not recognise fields the first one drew — so the member fills the form, presses Pay, and
   * is told their card could not be checked, with three perfectly valid iframes on the screen.
   */
  function providerClient(): Promise<ProviderClient> {
    client ??= load().catch(() => {
      // **The rejection value is discarded, deliberately and without being read.** A failure to
      // reach the provider is not a provider message about a member's card — it is a network
      // error, whose text is written for an engineer and can carry a URL, a stack or a hostname.
      // One fixed sentence goes on the screen instead, so nothing arriving from the outside is
      // ever rendered or attached to a thrown error from this path.
      unavailable = CARD_ENTRY_UNAVAILABLE;
      // Neither is cached as failed: a member who comes back gets a real second attempt.
      client = null;
      group = null;
      publish();
      throw new Error(unavailable);
    });
    return client;
  }

  function providerElements(): Promise<ProviderElements> {
    group ??= providerClient().then((stripe) => stripe.elements());
    return group;
  }

  function publish(): void {
    const validity = currentValidity();
    for (const listener of listeners) listener(validity);
  }

  function currentValidity(): CardFieldsValidity {
    if (unavailable !== null) return { complete: false, message: unavailable };
    // Every field the provider draws has to say it is complete. A field that has not reported yet
    // is not complete — the default is the answer that keeps the Pay control disabled, because
    // the failure that matters is a control that goes live over a field nobody filled in.
    const drawn = [...fields.values()];
    const complete = drawn.length > 0 && drawn.every((state) => state.complete);
    // The provider's own message about its own field, in the order the fields are drawn, so a
    // member reads the first thing wrong rather than the last thing touched. Never composed here.
    const message = drawn.find((state) => state.message !== null)?.message ?? null;
    return { complete, message };
  }

  return {
    mount(field, node) {
      const type = PROVIDER_ELEMENT_TYPES[field];
      if (type === null || type === undefined) {
        // Nothing to draw and nothing to tear down. The panel owns this field now; see
        // `PROVIDER_ELEMENT_TYPES`.
        return () => undefined;
      }

      fields.set(field, { complete: false, message: null });
      let disposed = false;

      void providerElements()
        .then((drawn) => {
          if (disposed) return;
          const element = drawn.create(type, { style: STRIPE_ELEMENT_APPEARANCE });
          elements.set(field, element);
          element.on('change', (change) => {
            const state = fields.get(field);
            if (state === undefined) return;
            state.complete = change.complete === true;
            // `change.error` is the same shape as any other provider error and is read the same
            // narrow way. `empty` deliberately produces no message: telling a member a field they
            // have not reached yet is incomplete is noise, and the Pay control already says so.
            state.message = providerMessage(change.error, null);
            publish();
          });
          element.mount(node);
        })
        .catch(() => {
          // Already recorded by `providerElements`, which published the unavailable state. Nothing
          // is logged and nothing is rethrown: an unhandled rejection here is a browser console
          // line containing whatever the provider put in the object, which is the payload AC-3
          // is about.
          if (!disposed) node.textContent = unavailable ?? CARD_ENTRY_UNAVAILABLE;
        });

      return () => {
        disposed = true;
        fields.delete(field);
        const element = elements.get(field);
        elements.delete(field);
        element?.unmount();
        element?.destroy();
      };
    },

    onValidityChange(listener) {
      listeners.add(listener);
      // Report immediately so a panel that mounts after the fields have settled — or after the
      // script has already failed — is not left waiting for a change that has already happened.
      listener(currentValidity());
      return () => {
        listeners.delete(listener);
      };
    },

    async createPaymentMethodToken(details) {
      const number = elements.get('number');
      if (number === undefined) throw new Error(unavailable ?? CARD_ENTRY_UNAVAILABLE);

      // The same client that drew the fields — see `providerClient`. A second one would not
      // recognise them.
      const stripe = await providerClient();

      /*
       * The provider reads its own three iframes, takes the one value it has no field for, and
       * answers with a reference.
       *
       * `details.nameOnCard` passes through this line and is not stored, copied, closed over or
       * logged. That matters most on the failure branch below: a provider error raised *after* a
       * name was supplied must not carry it back out, which is why the error is still reduced to
       * `message` alone and re-raised as a fresh `Error`. Nothing in this function ever puts the
       * name into an object that outlives the call.
       */
      const answer = await stripe.createPaymentMethod({
        type: 'card',
        card: number,
        billing_details: { name: details.nameOnCard },
      });

      if (answer.error !== undefined && answer.error !== null) {
        // The only line in this file that touches a provider failure, and it takes one string.
        // Not `throw answer.error`, not `new Error(msg, { cause: answer.error })` — either would
        // put `decline_code` and, on some shapes, the instrument's own attributes into an object
        // that a future `catch` could log. A fresh `Error` with a message and no cause cannot.
        throw new Error(providerMessage(answer.error, CARD_TOKEN_EXCHANGE_FAILED));
      }

      const id = answer.paymentMethod?.id;
      // A resolved answer with neither an error nor an identifier is a provider contract change,
      // and it must not read as success: the caller would `POST` `undefined` as the token and the
      // member would be told their payment failed for a reason nobody could act on.
      if (typeof id !== 'string' || id === '') throw new Error(CARD_TOKEN_EXCHANGE_FAILED);
      return id;
    },
  };
}

/**
 * Forget the cached script load.
 *
 * Exists for tests, which share one module registry across files and would otherwise inherit
 * whichever document the first of them appended a script tag to.
 */
export function resetProviderScriptLoadForTests(): void {
  scriptLoad = null;
}
